import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Label, Select, Spinner, TextInput } from "flowbite-react";
import { HiExternalLink } from "react-icons/hi";
import { auctionPropertiesThunks } from "../../app/store";
import { propertyCensusSnapshotsApi } from "../../api/propertyCensusSnapshotsApi";
import { parseMoneyToNumber } from "../../utils/numberParser";
import PropertyCensusInsights from "./PropertyCensusInsights";
import PropertyMediaQueueGrid from "./shared/PropertyMediaQueueGrid";
import PropertyMediaSavedGrid from "./shared/PropertyMediaSavedGrid";
import PropertyMediaUploadPanel from "./shared/PropertyMediaUploadPanel";
import { usePropertyMediaManager } from "./shared/usePropertyMediaManager";

const MEDIA_TYPE = "ZILLOW_MAP_IMAGE";
const HOA_OPTIONS = ["YES", "NO", "UNKNOWN"];

function buildZillowUrlFromAddress(address) {
  const trimmed = (address || "").trim();
  if (!trimmed) return "https://www.zillow.com/";
  return `https://www.zillow.com/homes/${encodeURIComponent(trimmed)}/`;
}

function buildZillowSoldUrlFromCoords(lat, lng) {
  const parsedLat = Number(lat);
  const parsedLng = Number(lng);

  if (!Number.isFinite(parsedLat) || !Number.isFinite(parsedLng)) {
    return "https://www.zillow.com/";
  }

  const delta = 0.002;

  const bounds = {
    west: parsedLng - delta,
    east: parsedLng + delta,
    south: parsedLat - delta,
    north: parsedLat + delta,
  };

  const searchQueryState = {
    pagination: {},
    isMapVisible: true,
    mapBounds: bounds,
    filterState: {
      sort: { value: "globalrelevanceex" },
      rs: { value: true },
      fsba: { value: false },
      fsbo: { value: false },
      nc: { value: false },
      cmsn: { value: false },
      auc: { value: false },
      fore: { value: false },
    },
    isListVisible: true,
    mapZoom: 17,
  };

  const query = encodeURIComponent(JSON.stringify(searchQueryState));

  return `https://www.zillow.com/homes/recently_sold/?searchQueryState=${query}`;
}

function buildBestZillowUrl(property) {
  const lat = property?.latitude;
  const lng = property?.longitude;

  if (lat !== null && lat !== undefined && lng !== null && lng !== undefined) {
    const coordsUrl = buildZillowSoldUrlFromCoords(lat, lng);
    if (coordsUrl !== "https://www.zillow.com/") return coordsUrl;
  }

  return buildZillowUrlFromAddress(property?.property_address);
}

export default function Step5Zillow({ propertyId, property, onSave }) {
  const dispatch = useDispatch();
  const userId = useSelector((state) => state.auth?.user?.id);

  const [form, setForm] = useState({
    total_value: property?.total_value ?? "",
    zillow_url: property?.zillow_url || "",
    sold_usd_per_sqft: property?.sold_usd_per_sqft ?? "",
    poa_hoa: property?.poa_hoa || "UNKNOWN",
    hoa_monthly_fee: property?.hoa_monthly_fee ?? "",
  });
  const [labelBase, setLabelBase] = useState("zillow");
  const [saving, setSaving] = useState(false);
  const [syncingCensus, setSyncingCensus] = useState(false);

  const hasCoordinates = Boolean(property?.latitude && property?.longitude);

  const {
    deletingId,
    onDeleteMedia,
    onFileInputChange,
    pasteBoxRef,
    queue,
    removeFromQueue,
    savedMedia,
    updateQueueLabel,
    uploadError,
    uploading,
    uploadQueue,
  } = usePropertyMediaManager({
    property,
    propertyId,
    onSave,
    mediaType: MEDIA_TYPE,
    labelBase,
    defaultClipboardBase: "zillow",
    userId,
  });

  useEffect(() => {
    setForm({
      total_value: property?.total_value ?? "",
      zillow_url: property?.zillow_url || "",
      sold_usd_per_sqft: property?.sold_usd_per_sqft ?? "",
      poa_hoa: property?.poa_hoa || "UNKNOWN",
      hoa_monthly_fee: property?.hoa_monthly_fee ?? "",
    });
  }, [
    propertyId,
    property?.sold_usd_per_sqft,
    property?.total_value,
    property?.zillow_url,
    property?.poa_hoa,
    property?.hoa_monthly_fee,
  ]);

  async function handleSyncCensus() {
    if (!propertyId || !hasCoordinates) return;

    setSyncingCensus(true);
    try {
      await propertyCensusSnapshotsApi.syncCensus(propertyId);
      onSave?.();
    } catch (error) {
      console.error(error);
    } finally {
      setSyncingCensus(false);
    }
  }

  async function saveFields() {
    if (!propertyId) return;
    setSaving(true);
    try {
      await dispatch(
        auctionPropertiesThunks.updateOne({
          id: propertyId,
          payload: {
            total_value: parseMoneyToNumber(form.total_value),
            zillow_url: form.zillow_url?.trim() || null,
            sold_usd_per_sqft: parseMoneyToNumber(form.sold_usd_per_sqft),
            poa_hoa: form.poa_hoa || "UNKNOWN",
            hoa_monthly_fee: parseMoneyToNumber(form.hoa_monthly_fee),
          },
        }),
      ).unwrap();

      onSave?.();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-wrap gap-2">
        <Button
          className="gap-2"
          color="dark"
          onClick={() =>
  window.open(
    buildBestZillowUrl(property),
    "_blank",
    "noopener,noreferrer",
  )
}
        >
          Zillow <HiExternalLink />
        </Button>

        <Button
          color="dark"
          onClick={handleSyncCensus}
          disabled={syncingCensus || !hasCoordinates}
        >
          {syncingCensus ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Syncing Census
            </span>
          ) : (
            "Sync Census Data"
          )}
        </Button>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <Label>Market Value (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.total_value}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                total_value: event.target.value,
              }))
            }
            placeholder="Ex: 275000"
          />
        </div>

        <div>
          <Label>Sold USD / Sqft</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.sold_usd_per_sqft}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                sold_usd_per_sqft: event.target.value,
              }))
            }
            placeholder="Ex: 185.45"
          />
        </div>

        <div>
          <Label>HOA</Label>
          <Select
            value={form.poa_hoa}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                poa_hoa: event.target.value,
              }))
            }
          >
            {HOA_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </div>

        <div className="md:col-span-3">
          <Label>HOA Monthly Fee (USD)</Label>
          <TextInput
            type="text"
            inputMode="decimal"
            value={form.hoa_monthly_fee}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                hoa_monthly_fee: event.target.value,
              }))
            }
            placeholder="Ex: 450.00"
          />
        </div>

        <div className="md:col-span-3">
          <Label>Zillow URL</Label>
          <TextInput
            value={form.zillow_url}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                zillow_url: event.target.value,
              }))
            }
            placeholder="https://www.zillow.com/homedetails/..."
          />
        </div>

        <div className="md:col-span-3">
          <Label>Nome base das imagens</Label>
          <TextInput
            value={labelBase}
            onChange={(event) => setLabelBase(event.target.value)}
            placeholder="Ex: zillow-map, comps, sold, estimate"
          />
        </div>
      </div>

      <PropertyCensusInsights
        snapshot={property?.censusSnapshots?.[0]}
        property={property}
      />

      <PropertyMediaUploadPanel
        helperText="Clique nesta caixa e cole. As imagens vão aparecer para pré-visualização antes de enviar."
        onFileInputChange={onFileInputChange}
        onSave={saveFields}
        onUpload={uploadQueue}
        pasteBoxRef={pasteBoxRef}
        queueLength={queue.length}
        saveButtonLabel="Salvar Step 5"
        saving={saving}
        title="Cole prints do Zillow aqui (Ctrl+V)"
        uploadError={uploadError}
        uploading={uploading}
      />

      <PropertyMediaQueueGrid
        items={queue}
        labelPlaceholder="Ex: comps-1"
        onChangeLabel={updateQueueLabel}
        onRemove={removeFromQueue}
      />

      <PropertyMediaSavedGrid
        deletingId={deletingId}
        items={savedMedia}
        onDelete={onDeleteMedia}
        sectionTitle="Imagens já anexadas (Zillow)"
        typeLabel="zillow"
      />
    </div>
  );
}
